import { Button, ButtonGroup, Card, IconButton, LuIcon, Separator, Text } from "@overmap-ai/blocks"
import {
	type IssueComment,
	type IssueCommentAttachment,
	type Stored,
	submittedAtModelSortFn,
	toUuidArray,
} from "@overmap-ai/common"
import { selectAttachmentsOfIssueComment, selectUserById } from "@overmap-ai/core"
import { UUIDFile, type UUIDPromise } from "@overmap-ai/forms"
import { useSort } from "@overmap-ai/hooks"
import { Placeholder } from "@tiptap/extensions"
import { EditorContent, useEditor } from "@tiptap/react"
import { cx } from "class-variance-authority"
import React, { memo, useCallback, useState } from "react"

import { textSizeCva } from "../../cva"
import { useConfirmDeleteDialog } from "../../hooks/utils"
import { modelIconMapping } from "../../mappings/icons"
import { sdk } from "../../sdk"
import { useAppSelector, useShallowEqaulSelector } from "../../store"
import { BASE_EXTENSIONS, LINK_NODES, MENTION_EXTENSIONS, useEditorContent, useEditorEditable } from "../../tiptap"
import { NewLineFloatingMenu, TextBubbleMenu } from "../../tiptap/components"
import { EditorContext } from "../../tiptap/context"
import { formatCompactDate } from "../../utils/date"
import {
	getAttachmentsToDelete,
	getFilesAndPromisesFromAttachments,
	getFilesAndPromisesToCreate,
	uuidFilesFromFiles,
} from "../../utils/file"
import { getUserDisplayLabel } from "../../utils/users"
import { AttachmentThumbnailList } from "../AttachmentThumbnailList/AttachmentThumbnailList"
import { AttachmentButton } from "../Buttons/AttachmentButton"
import { CommentFileThumbnailList } from "../CommentFileThumbnailList/CommentFileThumbnailList"
import { IssueCommentDropdownMenu } from "../DropdownMenus/IssueCommentDropdownMenu"
import { UserIcon } from "../Icons/UserIcon"
import type { IssueTimelineSize } from "./typings"

interface IssueTimelineIssueCommentItemProps {
	issueComment: Stored<IssueComment>
	size: IssueTimelineSize
}

export const IssueTimelineIssueCommentItem = memo((props: IssueTimelineIssueCommentItemProps) => {
	const { issueComment, size } = props

	const author = useAppSelector(selectUserById(issueComment.created_by ?? -1))
	const openConfirmDeleteDialog = useConfirmDeleteDialog()
	const attachments = useShallowEqaulSelector(selectAttachmentsOfIssueComment(issueComment.uuid))
	const [files, setFiles] = useState<(UUIDFile | UUIDPromise<UUIDFile>)[]>([])
	const [currentAttachments, setCurrentAttachments] = useState<Stored<IssueCommentAttachment>[]>([])

	const editorContent = useEditorContent(issueComment.content)

	const editor = useEditor(
		{
			editorProps: {
				attributes: {
					class: "outline-none",
				},
			},
			content: editorContent,
			editable: false,
			extensions: [
				...BASE_EXTENSIONS,
				...LINK_NODES,
				...MENTION_EXTENSIONS,
				Placeholder.configure({
					placeholder: "Edit comment",
				}),
			],
		},
		[issueComment.uuid],
	)

	const isEditing = useEditorEditable(editor)

	const handleEditClicked = useCallback(() => {
		editor.setEditable(true, true)
		editor.chain().focus().run()
		setFiles(getFilesAndPromisesFromAttachments(attachments, sdk.issueCommentAttachments))
		setCurrentAttachments(attachments)
	}, [editor, attachments])

	const handleDelete = useCallback(
		(issueComment: Stored<IssueComment>) => {
			openConfirmDeleteDialog({
				title: "Delete comment?",
				description: "Are you sure you want to delete this comment? This cannot be undone.",
				onAction: () => sdk.issueComments.delete(issueComment.uuid),
			})
		},
		[openConfirmDeleteDialog],
	)

	const handleResolvedChange = useCallback((issueComment: Stored<IssueComment>, resolved: boolean) => {
		void sdk.issueComments.update({
			uuid: issueComment.uuid,
			resolved,
		})
	}, [])

	const handleReset = useCallback(() => {
		editor.setEditable(false, true)
		editor.chain().blur().run()
		setFiles([])
		setCurrentAttachments([])
	}, [editor])

	const handleSubmit = useCallback(() => {
		void sdk.issueComments.update({
			uuid: issueComment.uuid,
			content: JSON.stringify(editor?.getJSON()),
		})

		const attachmentsToDelete = getAttachmentsToDelete(currentAttachments, files)

		if (attachmentsToDelete.length > 0) {
			void sdk.issueCommentAttachments.bulkDelete(toUuidArray(attachmentsToDelete))
		}

		const filesToCreate = getFilesAndPromisesToCreate(currentAttachments, files)

		if (filesToCreate.length > 0) {
			void sdk.issueCommentAttachments.bulkAdd(
				filesToCreate
					.filter((file) => file instanceof UUIDFile)
					.map((file) => ({
						file,
						issue_comment: issueComment.uuid,
					})),
			)
		}

		handleReset()
	}, [editor, issueComment.uuid, handleReset, files, currentAttachments])

	const handleCancel = useCallback(() => {
		editor.commands.setContent(editorContent)
		handleReset()
	}, [editor, editorContent, handleReset])

	const handleAttachFiles = useCallback(
		(files: File[]) => {
			setFiles((prev) => [...prev, ...uuidFilesFromFiles(files)])
		},
		[setFiles],
	)

	const handleDeleteFile = useCallback(
		(file: UUIDFile | UUIDPromise<UUIDFile>) => {
			setFiles(files.filter(({ uuid }) => file.uuid !== uuid))
		},
		[files, setFiles],
	)

	const isResolved = issueComment.resolved
	const sortedAttachments = useSort(attachments, submittedAtModelSortFn)

	return (
		<div className="flex w-full gap-4">
			<div className="flex shrink-0 flex-col items-center gap-2 overflow-hidden">
				<UserIcon userId={author?.id} size={size} />
				<Separator orientation="vertical" size="full" className="min-h-0 grow" />
			</div>

			<Card className="flex h-max min-w-0 grow flex-col gap-3" size={size}>
				{!isEditing ? (
					<>
						<div className="flex items-center justify-between gap-2">
							<div className="flex h-max min-w-0 grow items-center gap-2">
								<Text size={size} className="truncate whitespace-nowrap" accentColor="base">
									<Text highContrast>{getUserDisplayLabel(author)}</Text>
									&nbsp;commented&nbsp;&#8226;&nbsp;
									{formatCompactDate(issueComment.submitted_at)}
								</Text>
							</div>

							{!isEditing && (
								<ButtonGroup
									className="flex items-center justify-end gap-2"
									size={size}
									variant="ghost"
									accentColor="base"
								>
									{isResolved ? (
										<Text size={size} accentColor="base">
											Resolved
										</Text>
									) : null}
									<IssueCommentDropdownMenu
										variant="soft"
										accentColor="base"
										size={size}
										trigger={
											<IconButton>
												<LuIcon icon={modelIconMapping.more} />
											</IconButton>
										}
										issueComment={issueComment}
										onResolvedChange={handleResolvedChange}
										onEdit={handleEditClicked}
										onDelete={handleDelete}
									/>
								</ButtonGroup>
							)}
						</div>

						{!isResolved && <Separator size="full" />}
					</>
				) : null}

				{isEditing ? <CommentFileThumbnailList files={files} onDelete={handleDeleteFile} /> : null}

				{sortedAttachments.length > 0 && !isResolved && !isEditing ? (
					<AttachmentThumbnailList attachments={sortedAttachments} service={sdk.issueCommentAttachments} />
				) : null}

				{(!isResolved || isEditing) && (
					<EditorContext value={editor}>
						<EditorContent editor={editor} className={cx("min-h-sm h-max", textSizeCva({ size: size }))} />
						<NewLineFloatingMenu editor={editor} />
						<TextBubbleMenu editor={editor} />
					</EditorContext>
				)}

				{isEditing && (
					<ButtonGroup className="flex h-max w-full justify-between gap-2" size={size} variant="soft">
						<AttachmentButton
							icon
							variant="ghost"
							type="button"
							accentColor="base"
							onAttachFiles={handleAttachFiles}
							size={size}
						>
							<LuIcon icon={modelIconMapping.attachment} />
						</AttachmentButton>
						<div className="flex w-full justify-end gap-2">
							<Button accentColor="base" onClick={handleCancel}>
								<LuIcon icon={modelIconMapping.close} />
								Cancel
							</Button>
							<Button onClick={handleSubmit} variant="surface">
								<LuIcon icon={modelIconMapping.check} />
								Submit
							</Button>
						</div>
					</ButtonGroup>
				)}
			</Card>
		</div>
	)
})
IssueTimelineIssueCommentItem.displayName = "IssueTimelineIssueCommentItem"
